import { useState } from "react";
import { useLocation } from "react-router";

import { notFoundPath, postForm, unavailableMessage, type FieldErrors } from "../../api/client.js";
import { loginUrlAfterUnauthorized } from "../../app/session-expiry.js";
import { useGo } from "../../app/use-go.js";
import { focusField, useFieldCheck } from "../../forms/use-field-check.js";
import type { FormRules } from "../../forms/validation.js";
import { contractorRules } from "../../forms/view-model-rules.js";

import { postConfirm } from "./contract-api.js";
import type { Confirmation, ContractKind, FormValues, PlanChoice, PlanForm } from "./contract-types.js";
import {
    applyPlan, applySelected, bodyOf, changeValue, contractorFields, needsPayment, selectedPlan, visibleFields,
} from "./plan-form.js";
import { useConfirmEntry } from "./use-confirm-entry.js";

/** 申し込み・プラン変更の状態と、画面に渡すもの。 */
export interface ContractFlow {
    readonly step: "input" | "confirm";
    readonly values: FormValues;
    readonly plan: PlanChoice | undefined;
    readonly confirmation: Confirmation | undefined;
    readonly busy: boolean;
    /** 欄に結び付かない断り（隠れた欄の断りもここへ回す）。 */
    readonly summary: readonly string[];
    readonly fieldError: (name: string) => string | undefined;
    /** カード情報のトークン化に失敗した理由。 */
    readonly cardError: string | undefined;
    readonly change: (name: string, value: string) => void;
    readonly selectPlan: (plan: PlanChoice) => void;
    /** 欄を離れたとき（送る前の検証。値があれば見る）。 */
    readonly leave: (name: string) => void;
    /** 確認へ進む。<b>送る前に規則で見て、断った欄があれば送らない</b>（最初の欄へ移る）。 */
    readonly confirm: (form: HTMLFormElement) => void;
    /** 入力へ戻る（履歴を 1 つ戻る。ブラウザの「戻る」と同じ）。 */
    readonly back: () => void;
    /** 確定する（カードの要らない確定）。 */
    readonly commit: () => void;
    /**
     * カード払いの確定。<b>PAY.JP のボタンからトークンが返ったら呼ぶ</b>（<c>confirmation-view.tsx</c>。
     * ブラウザの確認ダイアログは出さない——PAY.JP の入力画面の送信が最後の確認）。トークンを添えて確定の口へ送る。
     */
    readonly commitWithCard: (token: string) => void;
    /** カードの入力を開いた・失敗したとき（断りを消す・出す）。 */
    readonly setCardError: (message: string | undefined) => void;
}

/**
 * 送る前に見る規則。<b>画面に並ぶ順</b>（最初に断った欄へ移る）——ご契約者の欄、最後に同意。
 * ご契約者の連絡先は有償プランで必須（無償プランでは任意で、API も求めない）。
 */
const rulesOf = (kind: ContractKind, plan: PlanChoice | undefined): FormRules => {
    const { Agree: agree, ...rest } = kind.rules;
    return {
        ...rest,
        ...(kind.contractorInput === true && contractorRules(needsPayment(plan))),
        ...(agree !== undefined && { Agree: agree }),
    };
};

/**
 * 入力 → 確認 → 確定（docs/05 §11.7）。
 *
 * <b>入力は画面が持つ</b>（サーバに状態を置かない。Implem.Portal.Backend の docs/05）。確認の口で金額・無料期間を受け取り、
 * 確定の口へ<b>同じ入力を送り直す</b>。確定の口は改めて全部検証する——確認で見せた内容は根拠にしない。
 * <c>extraFields</c> はその画面にしか無い欄（断りを欄の下に出すもの）。
 *
 * <b>確認は同じ URL のまま履歴に 1 段積む</b>（<c>use-confirm-entry.ts</c>）。ブラウザの「戻る」で入力へ戻る。
 * <b>入力を変えたら確認の結果を捨てる</b>——「進む」で古い金額の確認へ戻り、新しい入力で確定しないように。
 */
export const useContractFlow = (
    form: PlanForm, kind: ContractKind, extraFields: readonly string[],
): ContractFlow => {
    const go = useGo();
    // ログインが切れていたときの戻り先（今の画面）
    const { pathname, search } = useLocation();
    const here = pathname + search;

    // 開いたときにもプランの制約を当てる（サーバの初期値のままだと、固定の人数が 0 のまま出る）
    const [values, setValues] = useState<FormValues>(() => applySelected(form.plans, form.values));
    const [confirmation, setConfirmation] = useState<Confirmation | undefined>();
    const [errors, setErrors] = useState<FieldErrors>({});
    const [busy, setBusy] = useState(false);
    const [cardError, setCardError] = useState<string | undefined>();
    const plan = selectedPlan(form.plans, values);
    const check = useFieldCheck(rulesOf(kind, plan));
    const entry = useConfirmEntry(confirmation !== undefined);
    const step = entry.showsConfirm ? "confirm" : "input";

    const visible = visibleFields(plan, extraFields);
    // ご契約者の欄を入力しない画面は、API が返した保存済みの値を送らない
    const readOnly = kind.contractorInput === true ? [] : contractorFields;
    const inline = step === "input";

    const valueOf = (source: FormValues) => (name: string): string => source[name] ?? "";

    const confirm = async (): Promise<void> => {
        setBusy(true);
        setErrors({});
        try {
            const outcome = await postConfirm(kind.confirmUrl, bodyOf(values, undefined, readOnly));
            switch (outcome.kind) {
                case "confirmed": {
                    setConfirmation(outcome.value);
                    setCardError(undefined);
                    entry.push();
                    break;
                }
                case "unauthorized": {
                    go(loginUrlAfterUnauthorized(here, true));
                    return;
                }
                case "errors": {
                    setErrors(outcome.errors);
                    break;
                }
            }
        } catch {
            setErrors({ "": [unavailableMessage] });
        }
        setBusy(false);
    };

    const send = async (cardToken?: string): Promise<void> => {
        setBusy(true);
        setErrors({});
        try {
            const outcome = await postForm(kind.commitUrl, bodyOf(values, cardToken, readOnly));
            switch (outcome.kind) {
                case "go": {
                    go(outcome.location, { notice: outcome.notice, detail: outcome.detail });
                    return;
                }
                case "unauthorized": {
                    go(loginUrlAfterUnauthorized(here, true));
                    return;
                }
                case "notFound": {
                    go(notFoundPath);
                    return;
                }
                case "errors": {
                    setErrors(outcome.errors);
                    // 入力欄の断りなら入力へ戻す（断りを欄の下に出す）。
                    // 欄に結び付かない断り（カードの失敗など）は確認に残す
                    if (Object.keys(outcome.errors).some((name) => name !== "")) {
                        entry.back();
                    }
                    break;
                }
            }
        } catch {
            setErrors({ "": [unavailableMessage] });
        }
        setBusy(false);
    };

    return {
        step,
        values,
        plan,
        confirmation,
        busy,
        summary: Object.entries(errors)
            .filter(([name]) => !inline || !visible.has(name))
            .flatMap(([, messages]) => messages),
        // 送る前の断りを先に出す（直した欄の API の断りは、次に送るまで残る）
        fieldError: (name) =>
            (inline ? check.error(name) : undefined)
            ?? (inline && visible.has(name) ? errors[name]?.join(" ") : undefined),
        cardError,
        change: (name, value) => {
            const next = changeValue(values, name, value);
            setValues(next);
            setConfirmation(undefined);
            check.recheck(valueOf(next));
        },
        selectPlan: (choice) => {
            const next = applyPlan(values, choice);
            setValues(next);
            setConfirmation(undefined);
            check.recheck(valueOf(next));
        },
        leave: (name) => { check.leave(name, valueOf(values)); },
        confirm: (element) => {
            const invalid = check.checkAll(valueOf(values));
            if (invalid !== undefined) {
                // 送らない。前に送った断りは今の入力のものではないので消す
                setErrors({});
                focusField(element, invalid);
                return;
            }
            void confirm();
        },
        back: () => {
            setErrors({});
            entry.back();
        },
        commit: () => { void send(); },
        commitWithCard: (token) => {
            setCardError(undefined);
            void send(token);
        },
        setCardError,
    };
};
